import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useBuilder } from '../lib/builderStore';
import {
  QUESTION_TYPES, type FormElement, type QuestionType,
} from '../lib/types';
import { Icon } from '../components/icons';

/**
 * Editing a question where it sits, rather than in the properties panel.
 *
 * The panel still owns everything — validation, logic, PII, scoring — and stays the place
 * for the settings you touch once. What moves here is the handful of edits made constantly
 * while a form takes shape: the title, the options, whether it is required, and what kind
 * of question it is. Those were a round trip to the far side of the screen every time.
 *
 * Only the selected question expands. Every row expanding would turn a scannable list of
 * thirty questions into a page nobody can navigate.
 */

/** Mirrors the panel's rule: the value follows the label until somebody customises it. */
const slug = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

/** Types whose answers come from a list the author writes. */
const HAS_CHOICES = new Set<string>([
  'single_select', 'multi_select', 'dropdown', 'searchable_dropdown',
  'image_choice', 'likert', 'matrix_single', 'matrix_multi',
]);

/**
 * Edits one element wherever it lives — top level or inside a group.
 *
 * Exported because the summary row's Required toggle has to write the same field the same
 * way; two copies of this search would drift the moment one of them learned about a new
 * kind of nesting.
 */
export function useElementEdit(ref: string) {
  const apply = useBuilder((s) => s.apply);

  return (label: string, mutate: (el: FormElement) => void) =>
    apply(label, (draft) => {
      for (const page of draft.pages) {
        const target = page.elements.find((e) => e.ref === ref);
        if (target) { mutate(target); return; }

        for (const parent of page.elements) {
          const child = parent.elements?.find((c) => c.ref === ref);
          if (child) { mutate(child); return; }
        }
      }
    });
}

export function InlineEditor({ element }: { element: FormElement }) {
  const edit = useElementEdit(element.ref);
  const titleRef = useRef<HTMLInputElement>(null);

  // Editing starts on the title, because adding a question and naming it is one motion.
  useEffect(() => { titleRef.current?.focus(); }, [element.ref]);

  const choices = element.choices ?? [];
  const showsChoices = HAS_CHOICES.has(element.type ?? '');

  const setChoice = (index: number, label: string) =>
    edit('Edit choice', (el) => {
      const choice = el.choices?.[index];
      if (!choice) return;

      // The stored value follows the label only while it has not been customised: once a
      // response exists against a value, changing it would orphan that answer.
      const tracking = !choice.value || choice.value === slug(choice.label);
      choice.label = label;
      if (tracking) choice.value = slug(label);
    });

  return (
    <div
      className="border-t border-[var(--ff-border)] px-2.5 pb-2.5 pt-3"
      // The row above selects on click; clicks in here are edits, not selection changes.
      onClick={(e) => e.stopPropagation()}
    >
      {/*
        The type sits beside the title rather than in a bar of its own. It is the only
        control anywhere that changes a question's type — the properties panel has none —
        so it cannot simply be dropped, but it does not warrant its own row either.
      */}
      <div className="flex items-center gap-2">
        <label className="ff-sr" htmlFor={`t_${element.ref}`}>Question title</label>
        <input
          ref={titleRef}
          id={`t_${element.ref}`}
          value={element.title ?? ''}
          placeholder="Ask a question"
          onChange={(e) => edit('Rename question', (el) => { el.title = e.target.value; })}
          className="min-w-0 flex-1 rounded-md border border-[var(--ff-border-strong)]
                     bg-transparent px-2.5 py-1.5 text-sm font-medium outline-none
                     focus:border-[var(--ff-primary)]"
        />

        <label className="ff-sr" htmlFor={`ty_${element.ref}`}>Question type</label>
        <select
          id={`ty_${element.ref}`}
          value={element.type ?? ''}
          onChange={(e) => edit('Change question type', (el) => {
            const next = e.target.value as QuestionType;
            el.type = next;

            // A type that has no list must not keep one, and one that needs a list must
            // not arrive empty — an empty choice question blocks publishing.
            if (!HAS_CHOICES.has(next)) el.choices = null;
            else if (!el.choices?.length) {
              el.choices = [
                { value: 'option_1', label: 'Option 1' },
                { value: 'option_2', label: 'Option 2' },
              ];
            }
          })}
          className="ff-select w-36 shrink-0 rounded-md border border-[var(--ff-border)]
                     bg-transparent py-1.5 pl-2 text-[12.5px] text-[var(--ff-text-muted)]
                     hover:border-[var(--ff-border-strong)]"
        >
          {QUESTION_TYPES.filter((t) => !t.unavailable || t.type === element.type).map((t) => (
            <option key={t.type} value={t.type}>{t.label}</option>
          ))}
        </select>
      </div>

      {showsChoices && (
        <ul className="mt-2.5 space-y-1">
          {choices.map((choice, index) => (
            <li key={index} className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label={`Remove option ${index + 1}`}
                title="Remove option"
                disabled={choices.length <= 1}
                onClick={() => edit('Remove choice', (el) => { el.choices?.splice(index, 1); })}
                className="shrink-0 rounded p-0.5 text-[var(--ff-text-faint)]
                           hover:text-[var(--ff-danger)] disabled:opacity-30
                           disabled:hover:text-[var(--ff-text-faint)]"
              >
                <Icon name="close" size={13} />
              </button>

              <span
                aria-hidden="true"
                className={clsx(
                  'h-3.5 w-3.5 shrink-0 border border-[var(--ff-border-strong)]',
                  element.type === 'multi_select' ? 'rounded-[3px]' : 'rounded-full',
                )}
              />

              <input
                value={choice.label}
                aria-label={`Option ${index + 1}`}
                onChange={(e) => setChoice(index, e.target.value)}
                onKeyDown={(e) => {
                  // Enter adds the next option, so a list is typed without reaching for
                  // the mouse between each one.
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  edit('Add choice', (el) => {
                    const n = (el.choices?.length ?? 0) + 1;
                    el.choices = [
                      ...(el.choices ?? []).slice(0, index + 1),
                      { value: `option_${n}`, label: `Option ${n}` },
                      ...(el.choices ?? []).slice(index + 1),
                    ];
                  });
                }}
                className="min-w-0 flex-1 rounded border border-transparent bg-transparent
                           px-1.5 py-1 text-[13px] outline-none
                           hover:border-[var(--ff-border)]
                           focus:border-[var(--ff-primary)]"
              />
            </li>
          ))}

          <li>
            <button
              type="button"
              onClick={() => edit('Add choice', (el) => {
                const n = (el.choices?.length ?? 0) + 1;
                el.choices = [...(el.choices ?? []), { value: `option_${n}`, label: `Option ${n}` }];
              })}
              className="flex items-center gap-1.5 rounded px-1 py-1 text-[13px]
                         text-[var(--ff-primary)] hover:underline"
            >
              <Icon name="plus" size={13} /> Add option
            </button>
          </li>
        </ul>
      )}

    </div>
  );
}
